import { useState, type ComponentType, type ReactNode } from 'react';
import type { NostrEvent } from '@nostrify/nostrify';
import {
  Check,
  Copy,
  ExternalLink,
  Mail,
  MessageSquare,
  Share2,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { useToast } from '@/hooks/useToast';
import { shareableAppUrl, shareableNostrUri, socialShareUrls } from '@/lib/share';

interface ShareDialogProps {
  /** The wiki article, publication index, or publication section being shared. */
  event: NostrEvent;
  /** Display title used to prefill social-share text (e.g. `articleTitle(event)`). */
  title: string;
  children?: ReactNode;
}

/** A "share to X" button: an `<a>` styled as a Button when `href` is available, a disabled placeholder otherwise. */
function ShareLinkButton({
  href,
  icon: Icon,
  label,
  external,
  className,
}: {
  href: string | undefined;
  icon: ComponentType<{ className?: string }>;
  label: string;
  /** Opens in a new tab. Off for `mailto:`/`nostr:` links, which should navigate in place. */
  external?: boolean;
  className?: string;
}) {
  if (!href) {
    return (
      <Button variant="outline" size="sm" disabled className={className}>
        <Icon className="size-4" />
        {label}
      </Button>
    );
  }
  return (
    <Button variant="outline" size="sm" asChild className={className}>
      <a href={href} target={external ? '_blank' : undefined} rel={external ? 'noopener noreferrer' : undefined}>
        <Icon className="size-4" />
        {label}
      </a>
    </Button>
  );
}

/**
 * "Share" button + dialog for a wiki article or publication (index or
 * section): a bare web link anyone can open (works with or without a
 * Nostr client), a `nostr:naddr…` URI for opening directly in a Nostr
 * client, and one-click links to a few common social platforms.
 *
 * Both links point at the *specific author's version* being shared
 * (`naddr` encodes `kind:pubkey:d`), matching how Wikistar treats every
 * author's take on a title as independent — sharing never implies "the"
 * canonical version.
 */
export function ShareDialog({ event, title, children }: ShareDialogProps) {
  const { toast } = useToast();
  const [copiedLink, setCopiedLink] = useState(false);
  const [copiedNostr, setCopiedNostr] = useState(false);

  const appUrl = shareableAppUrl(event);
  const nostrUri = shareableNostrUri(event);
  const social = appUrl ? socialShareUrls(title, appUrl) : undefined;

  const copy = async (value: string, which: 'link' | 'nostr') => {
    try {
      await navigator.clipboard.writeText(value);
      if (which === 'link') {
        setCopiedLink(true);
        setTimeout(() => setCopiedLink(false), 2000);
      } else {
        setCopiedNostr(true);
        setTimeout(() => setCopiedNostr(false), 2000);
      }
      toast({ title: 'Copied to clipboard' });
    } catch {
      toast({ title: 'Failed to copy', variant: 'destructive' });
    }
  };

  return (
    <Dialog>
      <DialogTrigger asChild>
        {children ?? (
          <Button type="button" variant="outline" size="sm" className="gap-1.5" title="Share">
            <Share2 className="size-4" />
            Share
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Share "{title}"</DialogTitle>
          <DialogDescription>
            Share a link to this exact version, or open it directly in a Nostr client.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="share-link">Link</Label>
            <div className="flex gap-2">
              <Input id="share-link" value={appUrl ?? ''} readOnly onClick={(e) => e.currentTarget.select()} />
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="shrink-0"
                disabled={!appUrl}
                onClick={() => appUrl && copy(appUrl, 'link')}
                title="Copy link"
              >
                {copiedLink ? <Check className="size-4 text-green-600" /> : <Copy className="size-4" />}
              </Button>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="share-nostr">Nostr URI</Label>
            <div className="flex gap-2">
              <Input
                id="share-nostr"
                value={nostrUri ?? ''}
                readOnly
                className="font-mono text-xs"
                onClick={(e) => e.currentTarget.select()}
              />
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="shrink-0"
                disabled={!nostrUri}
                onClick={() => nostrUri && copy(nostrUri, 'nostr')}
                title="Copy nostr: URI"
              >
                {copiedNostr ? <Check className="size-4 text-green-600" /> : <Copy className="size-4" />}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Opens directly in any Nostr client that understands NIP-54 wiki articles.
            </p>
          </div>

          <Separator />

          <div className="space-y-1.5">
            <Label>Share to</Label>
            <div className="grid grid-cols-2 gap-2">
              <ShareLinkButton href={nostrUri} icon={ExternalLink} label="Nostr client" />
              <ShareLinkButton href={social?.x} icon={XLogo} label="X (Twitter)" external />
              <ShareLinkButton href={social?.facebook} icon={FacebookLogo} label="Facebook" external />
              <ShareLinkButton href={social?.reddit} icon={MessageSquare} label="Reddit" external />
              <ShareLinkButton href={social?.email} icon={Mail} label="Email" className="col-span-2" />
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** A minimal "X" wordmark glyph — lucide-react has no X/Twitter brand icon. */
function XLogo({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden>
      <path d="M18.9 2H22l-7.2 8.2L22.6 22h-6.8l-5.3-6.9L4.4 22H1.3l7.7-8.8L1 2h6.9l4.8 6.3L18.9 2Zm-2.4 18h1.9L7.6 4H5.6l10.9 16Z" />
    </svg>
  );
}

/** A minimal Facebook "f" glyph — lucide-react has no Facebook brand icon. */
function FacebookLogo({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden>
      <path d="M22 12.06C22 6.5 17.52 2 12 2S2 6.5 2 12.06c0 5 3.66 9.15 8.44 9.94v-7.03H7.9v-2.9h2.54V9.85c0-2.5 1.49-3.89 3.78-3.89 1.1 0 2.24.2 2.24.2v2.47h-1.26c-1.24 0-1.63.77-1.63 1.56v1.88h2.78l-.44 2.9h-2.34V22c4.78-.79 8.44-4.94 8.44-9.94Z" />
    </svg>
  );
}
